// oxlint-disable
import { isPlatformServer } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, PLATFORM_ID, type Signal } from '@angular/core';
import type { Hono } from 'hono';
import { hc } from 'hono/client';
import { firstValueFrom } from 'rxjs';
import { injectRequestContext } from './redirect';

interface NgHonoOptions {
    url: string;
    authToken?: Signal<string | false | undefined>;
    withCredentials?: boolean;
    credentials?: RequestCredentials;
}

function setHeaders(
    requestInit?: RequestInit,
    isServer = false,
    pxWorkerSecret?: string | null,
    authToken?: string | false,
): HttpHeaders {
    let headers = new HttpHeaders(
        isServer
            ? {
                  'user-agent': 'worker-client',
                  ...(pxWorkerSecret ? { 'x-px-worker-secret': pxWorkerSecret } : {}),
              }
            : undefined,
    );

    if (requestInit?.headers) {
        if (Array.isArray(requestInit.headers)) {
            for (const [header, value] of requestInit.headers) {
                headers = headers.set(header, value);
            }
        } else if (requestInit.headers instanceof Headers) {
            requestInit.headers.forEach((value, header) => {
                headers = headers.set(header, value);
            });
        } else {
            for (const [header, value] of Object.entries(requestInit.headers)) {
                headers = headers.set(header, value);
            }
        }
    }

    if (authToken) {
        headers = headers.set('Authorization', `Bearer ${authToken}`);
    }

    return headers;
}

export function injectAngularHonoClient<T extends Hono>(options: NgHonoOptions) {
    const http = inject(HttpClient);
    const isServer = isPlatformServer(inject(PLATFORM_ID));
    const reqContext = injectRequestContext();
    return hc<T>(options.url, {
        fetch: async (input, requestInit, _Environment) => {
            const headers = setHeaders(requestInit, isServer, reqContext?.pxWorkerSecret, options.authToken?.());

            const body =
                requestInit?.body && typeof requestInit.body === 'string'
                    ? JSON.parse(requestInit.body)
                    : requestInit?.body;

            return firstValueFrom(
                http.request(requestInit?.method ?? 'GET', input.toString(), {
                    body,
                    responseType: 'text',
                    observe: 'response',
                    headers,
                    withCredentials: options.withCredentials,
                }),
            ).then((resp) => {
                if (!resp.ok) {
                    throw new Error(`HTTP Client ${resp.status} ${resp.statusText}`);
                }
                const respHeaders: Record<string, string> = {};
                for (const key of resp.headers.keys()) {
                    respHeaders[key] = resp.headers.get(key) ?? '';
                }
                return new Response(resp.body, {
                    status: resp.status,
                    statusText: resp.statusText,
                    headers: respHeaders,
                });
            });
        },
    });
}
